import Notification from '@rio-cloud/rio-uikit/Notification';
import { useEffect, useState } from 'react';
import { getNavigationTips, deleteNavigationTip } from '../../services/navigationTips';
import { INavigationTip } from '../../types/navigationTips';

interface UseControllProps {
  searchValue: string;
  filterType: string;
  filterStatus: string;
}

export const useControll = ({ searchValue, filterType, filterStatus }: UseControllProps) => {
  const [navigationTips, setNavigationTips] = useState<INavigationTip[]>([]);
  const [filteredTips, setFilteredTips] = useState<INavigationTip[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [sortBy, setSortBy] = useState<keyof INavigationTip>('infractionType');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  const fetchNavigationTips = async () => {
    try {
      setLoading(true);
      const tips = await getNavigationTips();
      setNavigationTips(tips);
    } catch (error) {
      console.error('Erro ao buscar dicas de navegação:', error);
      setNavigationTips([]);
      Notification.error('Não foi possível carregar a lista de dicas de navegação.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTip = async (tipId: string) => {
    try {
      await deleteNavigationTip(tipId);
      Notification.success('Dica de navegação excluída com sucesso!');
      await fetchNavigationTips(); // Recarrega a lista após excluir
    } catch (error) {
      console.error('Erro ao excluir dica de navegação:', error);
      Notification.error('Erro ao excluir dica de navegação.');
    }
  };

  const handleLoadMore = () => {
    if (page < totalPages) {
      setPage(page + 1);
    }
  };

  useEffect(() => {
    fetchNavigationTips();
  }, []);

  useEffect(() => {
    let filtered = [...navigationTips];

    if (searchValue) {
      filtered = filtered.filter(tip =>
        tip.text.toLowerCase().includes(searchValue.toLowerCase()) ||
        tip.description.toLowerCase().includes(searchValue.toLowerCase()) ||
        tip.infractionType.toLowerCase().includes(searchValue.toLowerCase())
      );
    }

    if (filterType) {
      filtered = filtered.filter(tip => tip.infractionType === filterType);
    }

    if (filterStatus) {
      // Por enquanto, como não temos campo status, vamos filtrar por todos
      // filtered = filtered.filter(tip => tip.status === filterStatus);
    }

    filtered.sort((a, b) => {
      const aValue = a[sortBy];
      const bValue = b[sortBy];

      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return sortDir === 'asc'
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      return sortDir === 'asc'
        ? (aValue < bValue ? -1 : 1)
        : (bValue < aValue ? -1 : 1);
    });

    // Para "carregar mais", vamos mostrar todos os itens até a página atual
    const endIndex = page * pageSize;
    const paginatedTips = filtered.slice(0, endIndex);

    setFilteredTips(paginatedTips);
    setTotalPages(Math.ceil(filtered.length / pageSize));
  }, [navigationTips, searchValue, filterType, filterStatus, page, pageSize, sortBy, sortDir]);

  const getInfractionTypeCounts = () => {
    const counts: Record<string, number> = {};
    navigationTips.forEach(tip => {
      counts[tip.infractionType] = (counts[tip.infractionType] || 0) + 1;
    });
    return counts;
  };

  return {
    navigationTips: filteredTips,
    totalPages,
    page,
    pageSize,
    loading,
    sortBy,
    sortDir,
    setSortBy,
    setSortDir,
    setPage,
    fetchNavigationTips,
    handleDeleteTip,
    handleLoadMore,
    getInfractionTypeCounts,
  };
}; 